import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'components/shared';
import styled from 'styled-components';
import { getFieldValue, Placeholder } from '@sitecore-jss/sitecore-jss-react';
import cx from 'classnames';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import HeartIcon from 'components/icons/Heart';
import HeartFilled from 'components/icons/HeartFilled';
import { useSpring, animated } from 'react-spring';
import ShareIcon from 'components/icons/Share';
import useScrollDirection from 'utils/hooks/useScrollDirection';
import pageTypes from 'utils/pageTypes';
import breakpoints from 'utils/breakpoints';
import {
  useUiValue,
  setHasCommingSonnCommunity,
  openRegistrationModal,
  updateSubNavigationBarGeneralDataContext,
} from 'stores/uiContext';
import { shareThis, getUrl } from 'utils/shareThis';
import useFavorites from 'utils/hooks/useFavorites';
import { useSitecoreContext } from 'stores/sitecoreContext';
import quickStartLogo from 'assets/quickStartLogo.svg';
import { useCountryValue } from 'stores/country';
import { isValidTitle } from 'utils/validateTitle';
import { getDefaultFontSize } from 'utils/getDefaultFontSize';
import Calculator from 'components/icons/Calculator';
import Info from 'components/icons/Info';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import { getPriceDifferential } from 'utils/getPriceDifferential';
import OpenNewTab from 'components/icons/OpenNewTab';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';

import { Button } from 'components/components/atoms';
import { MortgageCalculatorModal } from 'components/components/organisms';

const StyledContainer = styled(Container)`
  ${({ minHeight, isMobile }) =>
    isMobile &&
    minHeight &&
    `  min-height:${minHeight}px;
  `}
`;

const Heading = styled.h1`
  @media ${breakpoints.md} {
    font-size: 2.875rem;
  }
`;
const PriceText = styled.p`
  @media ${breakpoints.md} {
    font-size: 2.875rem;
  }
`;

const Text = styled.p`
  letter-spacing: 0.14px;
`;

const StyledButton = styled(Button)`
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 3rem;
  padding-right: 3rem;
  height: 3.125rem;
`;

const Img = styled.img`
  width: 6.25rem;
  height: 3.75rem;
  border-radius: 0.625rem;
`;

const TooltipLogo = styled.div`
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  opacity: 0;
  width: 0;
  height: 0;
`;
const QuickStartLogoContainer = styled.div`
  &:hover ${TooltipLogo} {
    opacity: 1;
    height: auto;
    width: 50%;
    top: 4rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`;

const Tooltip = styled.div`
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

  @media ${breakpoints.md} {
    width: 19rem;
    left: 75%;
  }
`;

const getCTA = (rendering) => {
  try {
    const fields = rendering.placeholders['btn-cta'][0].fields;
    return getFieldValue(fields, 'buttonCta');
  } catch (e) {
    console.log(e);
    return {};
  }
};

const getUTourCTA = (rendering) => {
  try {
    const fields = rendering.placeholders['home-specs'][0].fields;
    return getFieldValue(fields, 'uTourFirstCtaLink');
  } catch (e) {
    console.log(e);
    return {};
  }
};

const TitleDetailsBlock = ({ fields, rendering }) => {
  const title = getFieldValue(fields, 'title');
  const startingPriceLabel = getFieldValue(fields, 'startingPriceLabel');
  const startingPrice = getFieldValue(fields, 'startingPrice');
  const monthlyPrice = getFieldValue(fields, 'monthlyPrice');
  const displayPreviousPrice = getFieldValue(fields, 'displayPreviousPrice');
  const previousPriceQMI = getFieldValue(fields, 'previousPriceQMI');
  const image = getFieldValue(fields, 'image') || {};
  const currentPageType = getFieldValue(fields, 'currentPageType');
  const lotJDEID = getFieldValue(fields, 'lotJDEID');
  const rvGarage = getFieldValue(fields, 'rvGarage');
  const preSellingHome = getFieldValue(fields, 'preSellingHome');
  const componentCountry = getFieldValue(fields, 'country');
  const communityStatus = getFieldValue(fields, 'communityStatus');
  const prequalifyText = getFieldValue(fields, 'prequalifyText');
  const prequalifyLink = getFieldValue(fields, 'prequalifyLink');
  const awardImage = getFieldValue(fields, 'awardImage');
  const awardText = getFieldValue(fields, 'awardText');

  const [{ country }] = useCountryValue();
  const [{ notificationHeight, nationalNotificationHeight }, dispatch] =
    useUiValue();
  const { showNewSchemaColor } = useNewColorSchemaOptions();
  const mortgageAmountsModal = getFieldValue(fields, 'mortgageAmountsModal');

  // Two independent gates, and the distinction is what lets the mortgage
  // content be server-rendered at all:
  //
  //   PAGE gate    - the page's own country, from the Layout Service payload.
  //                  Decides WHICH country's figures apply and whether the
  //                  modal markup renders. Known on the server, so the modal
  //                  reaches the SSR HTML for crawlers, which never open modals.
  //   VISITOR gate - the visitor's stored country preference. Decides only
  //                  whether the TRIGGER shows. Undefined on the server and on
  //                  the client's first render (AppRoot seeds it that way to
  //                  avoid React #418), so the trigger stays absent until after
  //                  mount, exactly as it behaves today.
  //
  // Both gates agree between the server and the first client render, so this
  // adds no hydration mismatch.
  const mortgageCountry = getFieldValue(fields, 'mortgageCountry');
  // mortgageCountry comes from the State item's Country field, resolved in
  // TitleDetailsBlockContentsResolver. Falls back to the older `country` field,
  // which only HomeService populates (QMI), so this is safe to ship before the
  // resolver change deploys.
  const pageMortgageCountry =
    mortgageCountry ||
    (componentCountry === 'Canada'
      ? 'CAN'
      : componentCountry === 'United States'
      ? 'USA'
      : null);
  const isCanadaPage = pageMortgageCountry === 'CAN';
  const mortgageAmountsForPage =
    mortgageAmountsModal &&
    (isCanadaPage
      ? mortgageAmountsModal.mortgageAmountsCanada
      : mortgageAmountsModal.mortgageAmountsUSA);
  const displayMortgageForPage = !!(
    pageMortgageCountry &&
    mortgageAmountsModal &&
    (isCanadaPage
      ? mortgageAmountsModal.displayMortgageModalCanada
      : mortgageAmountsModal.displayMortgageModalUSA)
  );
  // Requiring an exact country match -- rather than testing the other
  // country's display flag, as this used to -- keeps trigger and modal
  // consistent by construction. Those flags are seeded from the GLOBAL site
  // settings and only the page's own one is overwritten per market, so the old
  // test could show a Canadian visitor a trigger on a US page whose modal then
  // carried US figures.
  const showMortgageTrigger = !!(
    displayMortgageForPage &&
    country === pageMortgageCountry &&
    monthlyPrice &&
    monthlyPrice !== '$0' &&
    monthlyPrice !== '$'
  );

  const amountValue =
    startingPrice && startingPrice.replace('$', '').replace(/,/g, '');

  const tooltipText = mortgageAmountsForPage && mortgageAmountsForPage.tooltip;

  const quickStartHomesTooltip = getFieldValue(
    fields,
    'quickStartHomesTooltip',
  );
  const priceDifferentialText =
    country === 'USA'
      ? getFieldValue(fields, 'priceDifferentialTextUSA')
      : getFieldValue(fields, 'priceDifferentialTextCAN');

  const cta = getCTA(rendering) || {};
  const UTourCTA = getUTourCTA(rendering) || {};

  useEffect(() => {
    dispatch(
      updateSubNavigationBarGeneralDataContext({
        title,
        buttonCta: cta,
        currentPageType,
      }),
    );

    return () => {
      dispatch(
        updateSubNavigationBarGeneralDataContext({
          title: '',
          buttonCta: {},
          currentPageType: '',
        }),
      );
    };
  }, [
    title,
    cta && cta.href,
    cta && cta.target,
    cta && cta.text,
    currentPageType,
    dispatch,
  ]);

  const [showModalMortgageCalculator, setShowModalMortgageCalculator] =
    useState(false);
  const [
    showModalMortgageCalculatorTooltip,
    setShowModalMortgageCalculatorTooltip,
  ] = useState(false);
  const tooltipRef = useRef();
  useOnClickOutside(tooltipRef, () => {
    setTimeout(() => {
      setShowModalMortgageCalculator(false);
      setShowModalMortgageCalculatorTooltip(false);
    }, 100);
  });

  useEffect(() => {
    setTimeout(() => {
      if (communityStatus && communityStatus.toUpperCase() === 'COMING SOON') {
        dispatch(setHasCommingSonnCommunity(true));
      }
    });
  }, [communityStatus]);

  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  const { ContextID = {} } = useSitecoreContext() || {};
  const id = ContextID.value;
  const isFavorite = favoritesList.includes(id);

  const [{ notificationActive }] = useUiValue();

  const scrollDirection = useScrollDirection();

  const [fixedBarVisible, setFixedBarVisible] = useState(false);
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);
  const [defaultFontSize, setDefaultFontSize] = useState();
  useEffect(() => {
    const font = getDefaultFontSize();
    setDefaultFontSize(font);
  }, []);

  const [notificationTopHeight, setNotificationTopHeight] = useState(0);
  useEffect(() => {
    setNotificationTopHeight(notificationHeight + nationalNotificationHeight);
  }, [notificationHeight, nationalNotificationHeight]);

  const fixedBarStyle = useSpring({
    config: { duration: 100 },
    top: fixedBarVisible
      ? scrollDirection === 'up'
        ? defaultFontSize <= 18
          ? notificationTopHeight + 110 // 80
          : notificationTopHeight + 150
        : defaultFontSize <= 18
        ? notificationTopHeight
        : notificationTopHeight + 70
      : -100,
  });

  const handleOnScroll = () => {
    if (!isMobile) {
      const currentScrollY = window.scrollY;
      const offset = notificationActive ? 360 : 300;
      if (currentScrollY > offset) {
        setFixedBarVisible(true);
      } else if (fixedBarVisible) {
        setFixedBarVisible(false);
      }
    }
  };

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  const handleCTAClick = () => {
    trackAnalyticsEvent({
      gtagEvent: 'StickyNavFormClick',
      mouseflowTag: 'StickyNavForm',
    });
    dispatch(openRegistrationModal());
  };

  const [hasSitemap, setHasSitemap] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.document) {
        const exitsSitemap = window.document.getElementById('sitemap');
        if (exitsSitemap) setHasSitemap(true);
      }
    }, 0);
  }, []);

  const getCurrentLocation = () => {
    if (typeof window !== 'undefined' && window.document) {
      return window.location.pathname;
    }
    return '';
  };

  return (
    <>
      <animated.div
        id='detailsBlockBar'
        className={cx(
          'fixed w-screen top-0 flex bg-mattamy-blue-light items-center justify-between z-30 h-20',
          { hidden: !fixedBarVisible || isMobile },
        )}
        style={fixedBarStyle}
      >
        <div className='ml-4 flex justify-center items-center'>
          {image && image.src && (
            <div className='m-1'>
              <Img
                src={image.src}
                alt={image.alt}
                className='object-cover w-full h-full'
              ></Img>
            </div>
          )}
          <div className='mx-12 text-left'>
            {hasSitemap ? (
              <a href={getCurrentLocation() + '#sitemap'}>
                {isValidTitle(title) && (
                  <div className='underline mt-2 text-white font-trade-gothic-20 leading-9 tracking-tighter text-2xl leading-none'>
                    {title}
                  </div>
                )}
              </a>
            ) : (
              isValidTitle(title) && (
                <div className='mt-2 text-white font-trade-gothic-20 leading-9 tracking-tighter text-2xl leading-none'>
                  {title}
                </div>
              )
            )}
            <Placeholder
              name='home-specs'
              rendering={rendering}
              navBar
              lotJDEID={lotJDEID}
              rvGarage={rvGarage}
            />
          </div>
        </div>
        <div className='flex justify-center'>
          <div className='mr-8 text-right'>
            {startingPrice &&
              startingPrice !== '$0' &&
              startingPrice !== '$' && (
                <div className='mt-2 text-2xl text-white font-trade-gothic-20 tracking-tighter leading-none'>
                  {startingPrice}
                </div>
              )}

            {showMortgageTrigger && (
              <Text className='text-white font-graphie text-base'>
                {mortgageAmountsForPage.cardDisplayText + ' '}
                {mortgageAmountsForPage.cardDisplayPrice && monthlyPrice}
              </Text>
            )}
          </div>
          <div className='mx-2 flex items-center justify-center'>
            {UTourCTA && UTourCTA.text && (
              <StyledButton
                className='mr-2'
                color='white'
                textColor='action-blue'
                smallCTA
                role='link'
                href={UTourCTA.href}
                target={UTourCTA.target}
              >
                {UTourCTA.text}
              </StyledButton>
            )}

            {cta && cta.text && (
              <StyledButton
                color='white'
                textColor='action-blue'
                smallCTA
                onClick={handleCTAClick}
                role='link'
              >
                {cta.text}
              </StyledButton>
            )}
          </div>
          <div className='mr-8 flex justify-center items-center'>
            <div
              className='flex items-center justify-center cursor-pointer'
              onClick={() => handleFavoriteClick(id)}
              aria-label={
                isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'
              }
              aria-pressed={isFavorite ? 'true' : 'false'}
              role='button'
            >
              {isFavorite ? (
                <HeartFilled className='mx-2 h-5 w-5 text-white' />
              ) : (
                <HeartIcon className='mx-2 text-white' />
              )}
            </div>

            <div
              className='st-custom-button'
              onClick={() => {
                shareThis({
                  dataNetwork: 'email',
                  dataUrl: getUrl(),
                });
              }}
            >
              <ShareIcon className='mx-2 text-white' />
            </div>
          </div>
        </div>
      </animated.div>
      <StyledContainer className='justify-between pb-6'>
        <div
          className={cx(
            'flex flex-col md:flex-row md:justify-between pt-5 md:pb-6',
            {
              'md:items-baseline': !startingPriceLabel,
              'md:items-center': startingPriceLabel,
              'pb-2': isMobile && awardImage && awardImage.src && awardText,
              'pb-0': !isMobile,
            },
          )}
        >
          <div className='mb-6 md:mb-0'>
            {startingPriceLabel && (
              <div className='-mt-2 text-mattamy-blue h-3 md:h-8'></div>
            )}
            <div className='flex flex-row w-full'>
              {hasSitemap ? (
                <a href={getCurrentLocation() + '#sitemap'}>
                  <Heading
                    className={cx(
                      'underline flex text-3xl font-bold font-trade-gothic-20 leading-tiny mt-1',
                      {
                        'text-mattamy-blue ': !showNewSchemaColor,
                        'text-black ': showNewSchemaColor,
                      },
                    )}
                  >
                    <span>{title}</span>
                  </Heading>
                </a>
              ) : (
                <Heading
                  className={cx(
                    'flex text-3xl font-bold font-trade-gothic-20 leading-tiny mt-1',
                    {
                      'text-mattamy-blue ': !showNewSchemaColor,
                      'text-black ': showNewSchemaColor,
                    },
                  )}
                >
                  <span>{title}</span>
                </Heading>
              )}
              {currentPageType === pageTypes.QmiPage &&
                preSellingHome &&
                componentCountry === 'United States' && (
                  <>
                    <QuickStartLogoContainer className='rounded border-mattamy-blue-light border-2 h-6 mt-1 md:h-8 md:mt-2 ml-4'>
                      <img
                        className='object-cover h-full rounded'
                        src={quickStartLogo}
                        alt='quickStartLogo...'
                      />
                      {quickStartHomesTooltip && (
                        <TooltipLogo className='-ml-16 md:ml-0 absolute bg-white rounded z-10 text-left text-action-blue text-sm cursor-default'>
                          {quickStartHomesTooltip}
                        </TooltipLogo>
                      )}
                    </QuickStartLogoContainer>
                  </>
                )}
            </div>
            <Placeholder
              name='home-specs'
              rendering={rendering}
              lotJDEID={lotJDEID}
              rvGarage={rvGarage}
            />
          </div>

          <div
            className={cx('md:text-right ', {
              'text-mattamy-blue ': !showNewSchemaColor,
              'text-black ': showNewSchemaColor,
            })}
          >
            {startingPrice &&
              startingPrice !== '$0' &&
              startingPrice !== '$' && (
                <>
                  {startingPriceLabel && (
                    <p className='text-sm md:text-xl tracking-wide'>
                      {startingPriceLabel}
                    </p>
                  )}

                  <PriceText className='font-bold font-trade-gothic-20 text-3xl leading-tiny mt-1'>
                    <span>{startingPrice} </span>
                    {displayPreviousPrice && (
                      <span className='ml-4 line-through text-mattamy-gray-light'>
                        {previousPriceQMI}{' '}
                      </span>
                    )}
                    {displayPreviousPrice && (
                      <>
                        <span className='ml-4 font-graphie text-xs leading-2 -mt-2 text-mattamy-gray-light '>
                          {priceDifferentialText}{' '}
                        </span>
                        <span className='text-lg md:text-2xl leading-5 font-trade-gothic-20 text-mattamy-gray-light '>
                          {getPriceDifferential(
                            previousPriceQMI,
                            startingPrice,
                          )}
                        </span>
                      </>
                    )}
                  </PriceText>
                </>
              )}

            {showMortgageTrigger && (
              <div className='relative flex md:justify-end'>
                <Calculator className={'h-6'} />
                <button
                  className={cx(
                    'text-md font-trade-gothic-20 uppercase font-bold leading-8 md:leading-7 tracking-wide cursor-pointer hover:underline',
                  )}
                  tabIndex={0}
                  onClick={() => {
                    trackAnalyticsEvent({
                      gtagEvent: 'MortgageCalcLinkClick',
                      mouseflowTag: 'MortgageCalcLinkClick',
                    });
                    setShowModalMortgageCalculator(true);
                  }}
                >
                  {mortgageAmountsForPage.cardDisplayText + ' '}
                  {mortgageAmountsForPage.cardDisplayPrice && monthlyPrice}
                </button>
                <svg
                  className={cx('w-6 mb-1 mr-2 transform rotate-180')}
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    fill='currentColor'
                    d='M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z'
                  />
                </svg>
                {tooltipText && tooltipText.length > 0 && (
                  <button
                    className='mb-1'
                    onClick={(e) => {
                      setShowModalMortgageCalculatorTooltip(true);
                    }}
                  >
                    <Info className={' h-4 transform'} />
                  </button>
                )}
              </div>
            )}

            {/* Gated on the PAGE, not the visitor, so the dialog's markup --
                title, rates, term, estimated P&I, incentives copy and
                disclaimer -- is in the server HTML directly beneath the
                Estimated P&I line. AI crawlers do not open modals, and
                react-modal could never server-render, so this now uses a
                native <dialog>: closed, it keeps its children in the DOM. */}
            {displayMortgageForPage && (
              <MortgageCalculatorModal
                isOpen={showModalMortgageCalculator}
                onClose={() => {
                  setShowModalMortgageCalculator(false);
                }}
                country={pageMortgageCountry}
                amountValue={amountValue}
                title={mortgageAmountsForPage.mortgageTitle}
                disclaimer={mortgageAmountsForPage.disclaimerText}
                ctaTitle={mortgageAmountsForPage.ctaTitle}
                ctaText={mortgageAmountsForPage.ctaText}
                ctaURL={
                  mortgageAmountsForPage.ctaurl &&
                  mortgageAmountsForPage.ctaurl.href
                }
                downpaymentInit={mortgageAmountsForPage.paymentPercentage}
                interestRateInit={mortgageAmountsForPage.interestRate}
                loanTermInit={mortgageAmountsForPage.termInYears}
              />
            )}

            {showModalMortgageCalculatorTooltip && tooltipText && (
              <Tooltip
                className='absolute px-6 py-4 bg-white rounded z-10 text-left text-action-blue text-sm cursor-default'
                data-aos='fade-down'
                ref={tooltipRef}
              >
                {tooltipText}
              </Tooltip>
            )}

            {prequalifyText && prequalifyText && prequalifyLink.href && (
              <div className='flex md:flex-row-reverse'>
                <a
                  href={prequalifyLink.href}
                  onClick={(e) => {
                    e.nativeEvent.stopPropagation();
                    trackAnalyticsEvent({
                      gtagEvent: 'PreQualClick',
                      mouseflowTag: 'PreQual',
                    });
                  }}
                  target={prequalifyLink.target || '_self'}
                  className='h-full w-auto cursor-pointer text-center rounded-full px-4 pt-1 pb-2 bg-mattamy-blue text-white'
                >
                  <span className='flex flex-row justify-center text-sm text-center text-xs mt-1 font-trade-gothic-20 uppercase'>
                    {prequalifyText}
                    {prequalifyLink &&
                      prequalifyLink.target &&
                      prequalifyLink.target === '_blank' && (
                        <OpenNewTab className='w-3 -mt-1' />
                      )}
                  </span>
                </a>
              </div>
            )}
          </div>
        </div>
        {awardImage && awardImage.src && awardText && (
          <div className='bg-light-blue text-mattamy-blue rounded w-full p-1 flex items-center gap-3'>
            <img
              className='w-12 h-12 md:w-16 md:h-16 flex-shrink-0 bg-white rounded object-contain p-1'
              src={awardImage.src}
              alt={awardImage.alt}
            />
            <span className='ml-2 font-graphie'>{awardText}</span>
          </div>
        )}
      </StyledContainer>
    </>
  );
};

export default TitleDetailsBlock;
